// packages/frontend/src/pages/LoginPage.tsx
import { useState, useRef } from 'react'
import { useAuthStore } from '../stores/authStore'
import { AlertCircle } from 'lucide-react'
import { TurnstileComponent, TurnstileRef } from '../components/Turnstile'

export function LoginPage() {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string>('')
  const [turnstileError, setTurnstileError] = useState<string>('')
  const turnstileRef = useRef<TurnstileRef>(null)
  
  const { login, isLoading } = useAuthStore()

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token)
    setTurnstileError('')
  }

  const handleTurnstileError = () => {
    setTurnstileError('Verification failed. Please refresh the page.')
    setTurnstileToken('')
  }

  const handleTurnstileExpire = () => {
    setTurnstileToken('')
    setTurnstileError('Verification expired. Please verify again.')
    setTimeout(() => {
      turnstileRef.current?.reset()
    }, 100)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!password.trim()) {
      setTurnstileError('Please enter your password')
      return
    }

    if (!turnstileToken) {
      setTurnstileError('Please complete the security verification')
      return
    }

    const success = await login(password, turnstileToken)
    
    if (!success) {
      turnstileRef.current?.reset()
      setTurnstileToken('')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-950 px-4">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold text-white tracking-tight">
            Vendetta
          </h1>
        </div>

        {/* Login Form */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark-200 mb-2">
                Password
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onDoubleClick={() => setShowPassword(!showPassword)}
                className="input w-full"
                placeholder="Enter password"
                disabled={isLoading}
                autoFocus
                autoComplete="current-password"
              />
            </div>

            {/* Turnstile Field - Same Style as Password */}
            <div>
              <label className="block text-sm font-medium text-dark-200 mb-2">
                Security Verification
              </label>
              
              <div className="input flex items-center justify-center py-3 px-4 bg-dark-800/50">
                <TurnstileComponent
                  ref={turnstileRef}
                  onVerify={handleTurnstileVerify}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpire}
                  theme="dark"
                  size="normal"
                  action="login"
                />
              </div>
              
              {/* Error Message */}
              {turnstileError && (
                <div className="mt-3 flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>{turnstileError}</p>
                </div>
              )}
              
              {/* Success Indicator */}
              {turnstileToken && !turnstileError && (
                <div className="mt-3 text-sm text-green-400">
                  <p>Verified</p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !password.trim() || !turnstileToken}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 disabled:bg-dark-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              {isLoading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>

          {/* Footer Note */}
          <div className="mt-6 pt-6 border-t border-dark-700">
            <p className="text-xs text-center text-dark-400">
              Protected by Cloudflare Turnstile
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}